import DOMPurify from "dompurify";

import { htmlSanitizerPolicy } from "./htmlSanitizerPolicy";

type DomainAllowlist = readonly string[] | string[] | null | undefined;

function buildAllowedDomains(iframeDomainAllowlist?: DomainAllowlist): Set<string> {
  const hasExplicit = iframeDomainAllowlist && iframeDomainAllowlist.length > 0;
  const raw = (hasExplicit ? iframeDomainAllowlist : htmlSanitizerPolicy.fallbackIframeDomains) ?? [];
  return new Set<string>(raw.map((d) => d.toLowerCase().trim()).filter(Boolean));
}

function isHostnameAllowed(hostname: string, allowedDomains: Set<string>): boolean {
  const lower = hostname.toLowerCase();
  if (allowedDomains.has(lower)) return true;
  for (const domain of Array.from(allowedDomains)) {
    if (lower.endsWith(`.${domain}`)) return true;
  }
  return false;
}

const sanitizeRichTextHtml = (rawHtml: string, iframeDomainAllowlist?: DomainAllowlist): string => {
  if (!rawHtml) return "";

  const allowedDomains = buildAllowedDomains(iframeDomainAllowlist);

  if (typeof DOMPurify.removeHook === "function") {
    DOMPurify.removeHook("uponSanitizeElement");
  }

  if (typeof DOMPurify.addHook === "function") {
    DOMPurify.addHook("uponSanitizeElement", (node: Element, data: { tagName: string }) => {
      if (String(data.tagName).toLowerCase() !== "iframe") {
        return;
      }

      const src = (node as HTMLIFrameElement).getAttribute("src") ?? "";
      try {
        const hostname = new URL(src).hostname;
        if (!hostname || !isHostnameAllowed(hostname, allowedDomains)) {
          node.parentNode?.removeChild(node);
        }
      } catch {
        node.parentNode?.removeChild(node);
      }
    });
  }

  const clean = DOMPurify.sanitize(rawHtml, {
    ADD_TAGS: [...htmlSanitizerPolicy.extraAllowedTags],
    ADD_ATTR: [...htmlSanitizerPolicy.extraAllowedAttributes],
    FORBID_TAGS: [...htmlSanitizerPolicy.forbiddenTags],
  });

  if (typeof DOMPurify.removeHook === "function") {
    DOMPurify.removeHook("uponSanitizeElement");
  }

  return clean;
};

export function sanitizeHtmlByDescriptionFormat(
  format: string,
  html: string,
  iframeDomainAllowlist?: DomainAllowlist
): string {
  if (!html) {
    return "";
  }

  if (format !== "HTML") {
    return html;
  }

  return sanitizeRichTextHtml(html, iframeDomainAllowlist);
}

export type SanitizationRemoval = { type: "script"; count: number } | { type: "iframe"; hostname: string };

export function getSanitizationRemovals(
  rawHtml: string,
  iframeDomainAllowlist?: DomainAllowlist
): SanitizationRemoval[] {
  if (typeof document === "undefined" || !rawHtml?.trim()) return [];

  const allowedDomains = buildAllowedDomains(iframeDomainAllowlist);
  const removals: SanitizationRemoval[] = [];

  try {
    const doc = new DOMParser().parseFromString(rawHtml, "text/html");

    const scripts = doc.querySelectorAll("script");
    if (scripts.length > 0) removals.push({ type: "script", count: scripts.length });

    doc.querySelectorAll("iframe[src]").forEach((iframe) => {
      try {
        const hostname = new URL(iframe.getAttribute("src") ?? "").hostname;
        if (!isHostnameAllowed(hostname, allowedDomains)) {
          removals.push({ type: "iframe", hostname });
        }
      } catch {
        removals.push({ type: "iframe", hostname: "(invalid URL)" });
      }
    });
  } catch {
    /* ignore parse errors */
  }
  return removals;
}
